import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as parseColor from 'parse-color';
import { forkJoin, Observable, of } from 'rxjs';
import { LegalInfo } from '../interfaces/custom/legal';
import { ApiOrario, ImpostazioniRitiro } from '../interfaces/custom/orario';
import { MyTidyConfigService } from './my-tidy-config.service';
import { environment } from 'src/environments/environment';

// init_promise from site.init.js
declare var init_promise;
declare var api_base_url;

const theme_colors_map = {
    "accent-color": "accent_color",
    "accent-opposite-color": "accent_opposite_color",
    "light-color": "light_color",
    "light-opposite-color": "light_opposite_color",
    "light-opposite-muted-color": "light_opposite_muted",
    "light-divider-color": "light_divider",
    "departments-bg": "department_background",
    "departments-font-color": "department_font",
    "footer-bg": "footer_bg",
    "footer-fg": "footer_fg",
    "header-bg": "header_bg",
    "header-fg-muted": "header_fg_muted",
    "message-bg": "message_bg",
    "message-fg": "message_fg"
}

@Injectable({
    providedIn: 'root'
})
export class StoreService {
    unblock() {
        let d = new Date();
        d.setMinutes(d.getMinutes() + 30);
        this.unblockedUntil = d;
        localStorage.setItem('AjsncjUQjsmzlitj', JSON.stringify(this.unblockedUntil));
    }
    unblockedUntil;

    constructor(private http: HttpClient) {
        try {
            this.unblockedUntil = new Date(JSON.parse(localStorage.getItem('AjsncjUQjsmzlitj')));
        } catch (err) {
            let d = new Date();
            d.setDate(d.getDate() - 7);
            this.unblockedUntil = d;
        }
    }

    shopConfig;
    aziendaInfo;

    initColors(config) {
        let color_config = {};
        for (let color of Object.keys(theme_colors_map)) {
            color_config[color] = '#' + config[theme_colors_map[color]];
        }

        let ao = parseColor(color_config['accent-opposite-color']);
        if ((ao.rgb[0] * 0.299 + ao.rgb[1] * 0.587 + ao.rgb[2] * 0.114) > 186) {
            document.documentElement.style.setProperty(`--indicator-counter-font-color`, `#000`);
        }
        else {
            document.documentElement.style.setProperty(`--indicator-counter-font-color`, `#fff`);
        }

        let hbg = color_config['header-bg'];
        if (hbg && hbg.length > 3) {
            hbg = parseColor(hbg);
            if ((hbg.rgb[0] * 0.299 + hbg.rgb[1] * 0.587 + hbg.rgb[2] * 0.114) > 186) {
                document.documentElement.style.setProperty(`--header-fg`, `#000`);
            }
            else {
                document.documentElement.style.setProperty(`--header-fg`, `#fff`);
            }
        }

        let apply_color = (color_name) => {
            let colorString = color_config[color_name];
            if (!colorString || colorString.length < 4)
                return;

            let [h, s, l] = parseColor(colorString).hsl;
            document.documentElement.style.setProperty(`--${color_name}`, `hsl(${h}, ${s}%, ${l}%)`);
            document.documentElement.style.setProperty(`--${color_name}-h`, h);
            document.documentElement.style.setProperty(`--${color_name}-s`, s + '%');
            document.documentElement.style.setProperty(`--${color_name}-l`, l + '%');
        }

        for (let color_name of Object.keys(color_config))
            apply_color(color_name);
    }

    load() {
        return init_promise.then(r => {
            this.shopConfig = r.config;
            this.initColors(this.shopConfig);
            this.aziendaInfo = r.aziendaInfo;
            this.marche = r.marche;

            if (this.logo) {
                fetch(this.logo).catch(err => {
                    this.shopConfig['logo_url'] = '';
                });
              }
        })
    }

    get GetIdentificativo() {
        return this.shopConfig.http;
    }

    get main_page(): 'menu' | 'classic' {
        return this.shopConfig['tipo_tema'] == 1 ? 'menu' : 'classic';
    }

    get loginRequired(): boolean {
        return this.shopConfig['login_obbligatorio'];
    }

    get price(): "b2b" | "b2c" {
        return this.shopConfig['prezzi_ivati'] ? 'b2c' : 'b2b';
        // return this.shopConfig['prezzo_lordo'] ? "b2c" : "b2b";
    }

    get canale() {
        return this.shopConfig['canale']
    }

    get x_canale() {
        return {'X-CANALE': '' + this.canale}
    }

    get columns() {
        let cols = this.shopConfig['elementi_ricerca'];
        try {
            if (cols >= 3 && cols <= 5)
                return cols;
        } catch (err) {
            console.error(err);
        }
        return 4;
    }

    get filters_view_mode(): "sidebar" | "offcanvas" | "none" {
        let type = this.shopConfig['filtro_attivo'];
        if (type == '0')
            return 'none';
        if (type == '1')
            return 'sidebar';
        if (type == '2')
            return 'offcanvas';
        return "sidebar";
    }

    get carousel_time() {
        let time = this.shopConfig['carousel_time'];
        if (time && time > 10 || time < 1)
            return 3;
        return time;
    }

    get lat_lng() {
        return {
            'lat': this.aziendaInfo['latitudine'],
            'lng': this.aziendaInfo['longitudine'],
        }
    }

    get showPromotions() {
        return this.shopConfig['promozioni'];
    }

    get showNovita() {
        return this.shopConfig['novita'];
    }

    get showInEvidenza() {
        return this.shopConfig['prodotti_in_evidenza'];
    }

    get categorie_in_evidenza() {
        try {
            return JSON.parse(this.shopConfig['tag_evidence']);
        } catch (err) {

        }
        return [];
    }

    get slides() {
        return this.shopConfig.banner.map(banner => {
            return {
                "image_full": banner['url'],
                "image_mobile": banner['url_mobile']
            }
        });
    }

    get features() {
        return {
            "layout": "boxed",
            "items": this.shopConfig.items
        }
    }

    get UA_ID() {
        return this.shopConfig['google_analytics'];
    }

    get FACEBOOK_PIXEL() {
        return this.shopConfig['fb_pixel'];
    }

    get azienda_nome_univoco(): string {
        return this.aziendaInfo['nomeUnivoco'];
    }

    get logo(): string {
        return this.shopConfig['logo_url'];
    }

    get name(): string {
        return this.shopConfig.etichetta || this.aziendaInfo.ragioneSociale;
    }

    get email(): string {
        return this.aziendaInfo['email']
    }

    get address(): string {
        let indirizzo = this.aziendaInfo['indirizzo'];
        let cap = this.aziendaInfo['cap'];
        let citta = this.aziendaInfo['citta'];
        let provincia = this.aziendaInfo['provincia'];
        return `${indirizzo} - ${cap} ${citta} (${provincia})`;
    }

    get phone(): string[] {
        let telefono = this.aziendaInfo['contattoTelefonico'];
        if (telefono)
            return [telefono];
        return [];
    }

    get primaryPhone(): string | null {
        return this.phone.length > 0 ? this.phone[0] : null;
    }

    get partita_iva(): string {
        return this.aziendaInfo['partitaIva'];
    }

    get identificativo_azienda(): string {
        return this.aziendaInfo['identificativo_azienda'];
    }

    get userMessage() {
        return this.shopConfig.message_text;
    }

    get link_social() {
        let link_templates = {
            'facebook_link': { type: 'facebook', url: "", icon: 'fab fa-facebook-f' },
            'youtube_link': { type: 'youtube', url: "", icon: 'fab fa-youtube' },
            'instagram_link': { type: 'instagram', url: "", icon: 'fab fa-instagram' },
        };


        let get_link = (type, url) => {
            let link = link_templates[type];
            link.url = url;
            return link;
        }

        let links = ['facebook_link', 'youtube_link', 'instagram_link'];
        let social = [];
        for (let link_name of links) {
            let url = this.shopConfig[link_name];
            if (url) {
                social.push(get_link(link_name, url));
            }
        }

        if (social.length) {
            return social;
        }
    }

    get disponibilita() {
        return this.shopConfig.disponibilita;
    }

    get facebook_chat_id() {
        return this.shopConfig['facebook_chat_id'];
    }

    get _headers() {
        let public_token = localStorage.getItem('public_token');
        return {
            'Authorization': 'Bearer ' + public_token,
            ...this.x_canale
        }
    }

    getAziendaLegalInfo() : Observable<LegalInfo> {
        return this.http.get<LegalInfo>(api_base_url + 'Azienda/Legal', {
            headers: {
                'Business': this.azienda_nome_univoco,
                ...this._headers
            }
        });
    }

    getOrariDelNegozio() : Observable<ApiOrario[]> {
        return this.http.get<ApiOrario[]>(api_base_url + 'Azienda/Orari', {
            headers: {
                'Business': this.azienda_nome_univoco,
                ...this._headers
            }
        });
    }

    getImpostazioniDiRitiro() : Observable<ImpostazioniRitiro> {
        return this.http.get<ImpostazioniRitiro>(api_base_url + 'Azienda/ImpostazioniRitiro', {
            headers: {
                'Business': this.azienda_nome_univoco,
                ...this._headers
            }
        });
    }

    getStripeAccountID() {
        return this.aziendaInfo['stripe_code'];
    }

    get stripe_ok() {
        return this.aziendaInfo['stripe_is_ok'] && this.getStripeAccountID();
    }

    marche;

    getMarca(articolo) {
        if (this.marche)
            return this.marche.find(obj => obj.codice_marca == articolo.marca);
    }

}
